import { useQueryClient } from '@tanstack/react-query';
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode,
} from 'react';
import { DaghfalClient } from '../api/client';
import { ApiError } from '../api/problem';
import { limitsQuery } from '../hooks/LimitsProvider';
import { forgetPush } from '../notifications/webPush';
import type { CurrentSessionResponse, LegalAcceptance, LoginResponse, MembershipSummary } from '../api/types';
import { preferredMembership, type MembershipPreference } from './memberships';
import type { PermissionKey } from './permissions';
import { getPasskey } from './webauthn';

/**
 * Where the session is.
 *
 * `selecting` is its own state rather than a flag on `anonymous`: a user holding several memberships has
 * proved who they are but has not yet said which nursery they are acting in, and until they do, no tenant
 * data may be fetched (D-2, SRS §4.1.2).
 */
export type SessionStatus = 'loading' | 'anonymous' | 'selecting' | 'authenticated';

export interface SessionValue {
  status: SessionStatus;
  session: CurrentSessionResponse | null;

  /** The memberships to choose between, while `status` is `selecting`. */
  pendingMemberships: MembershipSummary[];

  /**
   * Signs in with a password. With `prefer`, from a nursery's entry link, a person with several memberships
   * goes straight into the one at that nursery where it is the only one this app opens (Q64).
   */
  signIn: (phone: string, password: string, prefer?: MembershipPreference) => Promise<SessionStatus>;
  /**
   * Signs in with a passkey: asks the API for a challenge, has the device sign it, and sends the signature.
   * `phone` narrows the device's offer to that account's passkeys; without it the device offers whichever it
   * holds for this site. Throws a `PasskeyError` when the device produced nothing (the person cancelled).
   * `prefer` as for `signIn`.
   */
  signInWithPasskey: (phone?: string | null, prefer?: MembershipPreference) => Promise<SessionStatus>;
  /**
   * Exchanges the selection token for a session at one membership. A 401 means the token is dead — spent,
   * expired, or outlived by a password change — so the selection ends with it: the offer is cleared and the
   * error rethrown, for the screen to say so and offer `cancelSelection`.
   */
  chooseMembership: (membershipId: number) => Promise<void>;
  /** Abandons a selection, back to signed out, so the person can sign in again. */
  cancelSelection: () => void;
  /**
   * Accepts an invitation. With `usePasskey`, proves an existing account with one of its passkeys rather
   * than `password`, which is then not sent: the device is asked for a signature first, which can throw a
   * `PasskeyError`.
   */
  acceptInvitation: (
    token: string, password: string | null, phoneCode?: string | null, usePasskey?: boolean,
    legal?: LegalAcceptance | null,
  ) => Promise<SessionStatus>;
  /** "This isn't my account": takes the invited number for a new account and accepts with it (Q25). */
  releaseInvitedNumber: (
    token: string, phoneCode: string, newPassword: string, legal?: LegalAcceptance | null,
  ) => Promise<SessionStatus>;
  /**
   * Ends the session. Push is forgotten first, so the next person on a shared phone is not pushed this one's
   * notifications (Q68), except with `keepPush`: on the way to signing straight in again, where the next sign-in
   * takes the subscription over (`usePushRefresh`, and a registration moves a device to whoever made it).
   */
  signOut: (options?: { keepPush?: boolean }) => Promise<void>;
  /** Re-reads `/auth/me` — after a role change, or a support session starting or ending. */
  reload: () => Promise<void>;

  can: (permission: PermissionKey) => boolean;
  canAny: (...permissions: PermissionKey[]) => boolean;
  canAll: (...permissions: PermissionKey[]) => boolean;
}

const SessionContext = createContext<SessionValue | null>(null);

export interface SessionProviderProps {
  client: DaghfalClient;
  children: ReactNode;
}

export function SessionProvider({ client, children }: SessionProviderProps) {
  const [status, setStatus] = useState<SessionStatus>(() =>
    client.tokens.read()?.accessToken ? 'loading' : 'anonymous');
  const [session, setSession] = useState<CurrentSessionResponse | null>(null);
  const [pendingMemberships, setPendingMemberships] = useState<MembershipSummary[]>([]);

  // Held in a ref rather than in state: it is a credential used by exactly one call, and putting it in
  // state would make it a render input and tempt something into displaying it.
  const selectionToken = useRef<string | null>(null);

  const queryClient = useQueryClient();

  // The membership whose data the query cache holds, or null for nobody.
  const cacheOwner = useRef<number | null>(null);

  /**
   * Makes `next` the session (and the status that follows from it), first dropping whatever the cache holds
   * for a different one.
   *
   * Query keys say what was fetched (`['me', 'children']`), not for whom, so without this the next person
   * on a shared phone is shown the last one's children from memory, under their own nursery's name, until
   * the entry goes stale. Ending the session clears the cache: the signed-in screens are about to unmount,
   * and refetching with no token would only be refused. Becoming another membership (an invitation
   * accepted while signed in, or another tab signing in as someone else) resets it instead, because the
   * screens may still be mounted and must repaint from nothing and refetch under the new token. Arriving
   * from no session needs neither: the cache was cleared when the last one ended.
   */
  const adopt = useCallback((next: CurrentSessionResponse | null) => {
    const owner = next?.membership.membershipId ?? null;
    if (cacheOwner.current !== null && cacheOwner.current !== owner) {
      if (owner === null) queryClient.clear();
      else void queryClient.resetQueries();
    }
    cacheOwner.current = owner;
    setSession(next);
    setStatus(next ? 'authenticated' : 'anonymous');
  }, [queryClient]);

  const load = useCallback(async (signal?: AbortSignal) => {
    if (!client.tokens.read()?.accessToken) {
      adopt(null);
      return;
    }
    // Beside `/auth/me` rather than after it: `RequireSession` waits for both, and the limits need only a token.
    const limits = limitsQuery(client);
    void queryClient.prefetchQuery(limits);
    try {
      const me = await client.me(signal);
      adopt(me);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      // A refusal means this session is finished. A network failure does not, but there is nothing useful
      // to show without `/auth/me` either, so both land on sign-in and the user retries.
      if (error instanceof ApiError) client.tokens.clear();
      // A refused prefetch would otherwise greet the next sign-in with its error.
      queryClient.removeQueries({ queryKey: limits.queryKey, type: 'inactive' });
      adopt(null);
    }
  }, [client, adopt, queryClient]);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  // Another tab signing out must not leave this one showing a nursery's children.
  useEffect(() => client.tokens.subscribe(() => { void load(); }), [client, load]);

  /**
   * What follows a password or passkey sign-in: a session, or the memberships to choose between. From a
   * nursery's entry link, the one membership there is chosen without asking; if the API refuses that, the
   * person chooses as usual.
   */
  const afterSignIn = useCallback(async (result: LoginResponse, prefer?: MembershipPreference): Promise<SessionStatus> => {
    if (result.status === 'membership_selection_required') {
      const preferred = prefer && result.selectionToken
        ? preferredMembership(result.memberships ?? [], prefer)
        : undefined;
      if (preferred && result.selectionToken) {
        try {
          await client.selectMembership(preferred.membershipId, result.selectionToken);
          selectionToken.current = null;
          setPendingMemberships([]);
          await load();
          return 'authenticated';
        } catch {
          // Fall through to the chooser: the token may still serve, and if it does not, the chooser says so.
        }
      }

      selectionToken.current = result.selectionToken;
      setPendingMemberships(result.memberships ?? []);
      setStatus('selecting');
      return 'selecting';
    }

    selectionToken.current = null;
    setPendingMemberships([]);
    await load();
    return 'authenticated';
  }, [client, load]);

  const signIn = useCallback(
    async (phone: string, password: string, prefer?: MembershipPreference): Promise<SessionStatus> =>
      afterSignIn(await client.login(phone, password), prefer),
    [client, afterSignIn]);

  const signInWithPasskey = useCallback(
    async (phone: string | null = null, prefer?: MembershipPreference): Promise<SessionStatus> => {
      const options = await client.passkeys.assertionOptions({ phone });
      const credentialJson = await getPasskey(options);
      return afterSignIn(await client.signInWithPasskey(credentialJson), prefer);
    }, [client, afterSignIn]);

  const chooseMembership = useCallback(async (membershipId: number) => {
    const token = selectionToken.current;
    if (!token) throw new Error('No membership selection is in progress.');
    try {
      await client.selectMembership(membershipId, token);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        selectionToken.current = null;
        setPendingMemberships([]);
      }
      throw error;
    }
    selectionToken.current = null;
    setPendingMemberships([]);
    await load();
  }, [client, load]);

  const cancelSelection = useCallback(() => {
    selectionToken.current = null;
    setPendingMemberships([]);
    setStatus('anonymous');
  }, []);

  const acceptInvitation = useCallback(
    async (
      token: string, password: string | null, phoneCode: string | null = null, usePasskey = false,
      legal: LegalAcceptance | null = null,
    ): Promise<SessionStatus> => {
      // The invitee's number is masked in the preview, so the options are asked for by the invitation itself:
      // the device then offers the invited account's passkeys alone, and on a shared tablet cannot present
      // somebody else's.
      const passkey = usePasskey
        ? await getPasskey(await client.passkeys.assertionOptions({ invitationToken: token }))
        : null;
      // The passkey proves the account instead of the password, so a password typed before choosing the
      // passkey stays on the device.
      await client.acceptInvitation(token, usePasskey ? null : password, phoneCode, passkey, legal);
      await load();
      return 'authenticated';
    }, [client, load]);

  const releaseInvitedNumber = useCallback(
    async (token: string, phoneCode: string, newPassword: string, legal: LegalAcceptance | null = null): Promise<SessionStatus> => {
      await client.releaseInvitedNumber(token, phoneCode, newPassword, legal);
      await load();
      return 'authenticated';
    }, [client, load]);

  const signOut = useCallback(async (options?: { keepPush?: boolean }) => {
    // While the token still works: a shared phone must not keep this person's pushes (Q68).
    if (!options?.keepPush) await forgetPush(client);
    await client.logout();
    selectionToken.current = null;
    setPendingMemberships([]);
    adopt(null);
  }, [client, adopt]);

  const value = useMemo<SessionValue>(() => {
    const granted = new Set(session?.permissions ?? []);
    const can = (permission: PermissionKey) => granted.has(permission);
    return {
      status,
      session,
      pendingMemberships,
      signIn,
      signInWithPasskey,
      chooseMembership,
      cancelSelection,
      acceptInvitation,
      releaseInvitedNumber,
      signOut,
      reload: () => load(),
      can,
      canAny: (...permissions) => permissions.some(can),
      canAll: (...permissions) => permissions.every(can),
    };
  }, [status, session, pendingMemberships, signIn, signInWithPasskey, chooseMembership, cancelSelection, acceptInvitation, releaseInvitedNumber, signOut, load]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession was called outside a <SessionProvider>.');
  return value;
}

/** True when the caller holds the permission. Convenience for a single check in a component. */
export function usePermission(permission: PermissionKey): boolean {
  return useSession().can(permission);
}
